import { useCallback, useState } from "react";

import { request } from "@/api/client";
import MCMasterContactRegistry from "@/components/master_control/MCMasterContactRegistry";
import type { MasterContactRegistryEntry, MasterContactRegistryUpsert } from "@/hooks/useMasterControl";
import { Link, useLocation } from "react-router-dom";

type RegistryResponse = { entries?: MasterContactRegistryEntry[] };

export default function RegistryPage() {
  const location = useLocation();
  const [entries, setEntries] = useState<MasterContactRegistryEntry[]>([]);
  const isInvitesActive = location.pathname.startsWith("/control/master/invites");
  const isRegistryActive = location.pathname.startsWith("/control/master/registry");
  const isOverviewActive = !isInvitesActive && !isRegistryActive;

  const fetchEntries = useCallback(
    async (query?: string, limit = 200, offset = 0) => {
      const params = new URLSearchParams();
      if (query) params.set("query", query);
      params.set("limit", String(limit));
      params.set("offset", String(offset));
      const payload = await request<RegistryResponse>(`/master-control/contact-registry?${params.toString()}`);
      setEntries(Array.isArray(payload?.entries) ? payload.entries : []);
    },
    [],
  );

  const upsertEntry = useCallback(
    async (payload: MasterContactRegistryUpsert) => {
      const response = await request<MasterContactRegistryEntry>("/master-control/contact-registry", {
        method: "POST",
        body: payload,
      });
      await fetchEntries();
      return response ?? null;
    },
    [fetchEntries],
  );

  const updateEntry = useCallback(
    async (id: string, payload: MasterContactRegistryUpsert) => {
      const response = await request<MasterContactRegistryEntry>(
        `/master-control/contact-registry/${encodeURIComponent(id)}`,
        { method: "PUT", body: payload },
      );
      await fetchEntries();
      return response ?? null;
    },
    [fetchEntries],
  );

  const deleteEntry = useCallback(
    async (id: string) => {
      await request(`/master-control/contact-registry/${encodeURIComponent(id)}`, { method: "DELETE" });
      await fetchEntries();
    },
    [fetchEntries],
  );

  return (
    <div className="aa-page-shell space-y-6">
      <header>
        <p className="text-xs uppercase tracking-wider text-muted-foreground">Master Control</p>
        <h1 className="text-3xl font-semibold text-foreground">Master contact registry</h1>
        <p className="text-sm text-muted-foreground">Global venue contact overrides managed by owners.</p>
      </header>
      <div className="flex flex-wrap gap-3 text-xs uppercase tracking-[0.3em] text-muted-foreground">
        <Link
          to="/control/master"
          className={
            isOverviewActive
              ? "rounded-full border border-primary/60 bg-primary/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground transition hover:bg-primary/20"
              : "rounded-full border border-transparent px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground/70 transition hover:border-border/40 hover:text-foreground"
          }
        >
          Overview
        </Link>
        <Link
          to="/control/master/invites"
          className={
            isInvitesActive
              ? "rounded-full border border-primary/60 bg-primary/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground transition hover:bg-primary/20"
              : "rounded-full border border-transparent px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground/70 transition hover:border-border/40 hover:text-foreground"
          }
        >
          Invites
        </Link>
        <Link
          to="/control/master/registry"
          className={
            isRegistryActive
              ? "rounded-full border border-primary/60 bg-primary/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground transition hover:bg-primary/20"
              : "rounded-full border border-transparent px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground/70 transition hover:border-border/40 hover:text-foreground"
          }
        >
          Registry
        </Link>
      </div>
      <div className="rounded-2xl border border-border bg-background/60 p-4 text-sm text-muted-foreground">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">Owner Endpoints</div>
        <div className="mt-2 grid gap-1 text-sm text-foreground/80">
          <div>
            <code>POST /master-control/contact-registry</code> create or upsert an entry
          </div>
          <div>
            <code>PUT /master-control/contact-registry/&lt;id&gt;</code> update an entry
          </div>
          <div>
            <code>DELETE /master-control/contact-registry/&lt;id&gt;</code> remove an entry
          </div>
        </div>
      </div>
      <MCMasterContactRegistry
        entries={entries}
        fetchEntries={fetchEntries}
        upsertEntry={upsertEntry}
        updateEntry={updateEntry}
        deleteEntry={deleteEntry}
      />
    </div>
  );
}
